<template>
  <div class="animated fadeIn analytics-dash">

    <!-- Header -->
    <div class="dash-header d-flex flex-wrap align-items-center mb-3">
      <div class="mr-auto">
        <h5 class="dash-title mb-0"><i class="icon-chart mr-2"></i>GAD Analytics</h5>
        <small class="text-muted">
          Growth data from <code>tipsoi_analitics</code>
          <span v-if="latestDate" class="ml-1">· latest snapshot <strong>{{ latestDate }}</strong></span>
        </small>
      </div>
      <div class="d-flex align-items-center mt-2 mt-md-0">
        <b-form-select
          v-model="days"
          :options="dayOptions"
          size="sm"
          class="range-select mr-2"
          :disabled="loading"
          @change="load"
        ></b-form-select>
        <b-button size="sm" variant="outline-secondary" @click="load" :disabled="loading">
          <i class="icon-refresh" :class="{ 'spin': loading }"></i> Refresh
        </b-button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading">
      <b-row class="kpi-row">
        <b-col v-for="n in 4" :key="'sk' + n" sm="6" xl="3" class="mb-3">
          <div class="kpi-card skeleton-card"></div>
        </b-col>
      </b-row>
      <div class="skeleton-card skeleton-chart"></div>
    </div>

    <!-- GAD not configured -->
    <b-alert v-else-if="unavailable" show variant="warning" class="mb-4">
      <i class="icon-info mr-2"></i>
      <strong>GAD datasource not configured</strong> — Analytics data is unavailable on this environment.
      Add <code>gad.datasource.url</code> credentials to your properties file to enable this feature.
    </b-alert>

    <div v-else>

      <!-- KPI strip — fleet & workforce -->
      <b-row class="kpi-row">
        <b-col v-for="c in cards" :key="c.key" sm="6" xl="3" class="mb-3">
          <div class="kpi-card" :style="{ '--accent': c.color }">
            <div class="kpi-top d-flex align-items-center justify-content-between">
              <span class="kpi-label">{{ c.label }}</span>
              <span v-if="c.delta !== null" class="kpi-delta" :class="c.deltaClass" title="Change over the selected period">
                <i :class="c.deltaIcon"></i>{{ c.deltaText }}
              </span>
            </div>
            <div class="kpi-value">{{ fmt(c.value) }}</div>
            <svg class="kpi-spark" viewBox="0 0 120 32" preserveAspectRatio="none" aria-hidden="true">
              <path v-if="c.spark.area" :d="c.spark.area" class="spark-area" :fill="c.color"></path>
              <polyline v-if="c.spark.points" :points="c.spark.points" class="spark-line" :stroke="c.color"></polyline>
              <circle v-if="c.spark.points" :cx="c.spark.cx" :cy="c.spark.cy" r="2.2" :fill="c.color"></circle>
            </svg>
          </div>
        </b-col>
      </b-row>

      <!-- Engagement strip — attendance & feature adoption -->
      <b-row class="kpi-row">
        <b-col v-for="m in engagement" :key="m.key" sm="6" xl="3" class="mb-3">
          <div class="mini-stat" :style="{ '--accent': m.color }">
            <div class="mini-icon" :style="{ background: m.color }"><i :class="m.icon"></i></div>
            <div class="mini-body">
              <div class="mini-value">{{ m.value }}</div>
              <div class="mini-label">{{ m.label }}</div>
              <div class="mini-sub">{{ m.sub }}</div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Growth trend + device utilization -->
      <b-row class="mb-3">
        <b-col lg="8" class="mb-3 mb-lg-0">
          <b-card class="panel h-100" body-class="panel-body">
            <div slot="header" class="panel-head">
              <span><i class="icon-graph mr-2"></i>Growth Trend</span>
              <small class="text-muted">Last {{ days }} days</small>
            </div>
            <div class="chart-wrap"><canvas ref="growthChart"></canvas></div>
          </b-card>
        </b-col>
        <b-col lg="4">
          <b-card class="panel h-100" body-class="panel-body">
            <div slot="header" class="panel-head"><span><i class="icon-pie-chart mr-2"></i>Device Utilization</span></div>
            <div class="util-wrap">
              <div class="util-ring">
                <svg viewBox="0 0 120 120">
                  <circle class="ring-track" cx="60" cy="60" r="52"></circle>
                  <circle class="ring-value" cx="60" cy="60" r="52" :stroke-dasharray="ringDash" stroke-dashoffset="0"></circle>
                </svg>
                <div class="util-center">
                  <div class="util-pct">{{ utilization }}%</div>
                  <div class="util-cap">active</div>
                </div>
              </div>
              <div class="util-legend">
                <div class="util-item"><span class="dot dot-active"></span>Active<strong class="ml-auto">{{ fmt(summary.totalActiveDevices) }}</strong></div>
                <div class="util-item"><span class="dot dot-idle"></span>Idle<strong class="ml-auto">{{ fmt(idleDevices) }}</strong></div>
                <div class="util-item util-total"><span class="dot dot-total"></span>Total<strong class="ml-auto">{{ fmt(summary.totalDevices) }}</strong></div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Client acquisition + attendance trend -->
      <b-row class="mb-3">
        <b-col lg="6" class="mb-3 mb-lg-0">
          <b-card class="panel h-100" body-class="panel-body">
            <div slot="header" class="panel-head">
              <span><i class="icon-user-follow mr-2"></i>Client Acquisition</span>
              <small class="text-muted">new clients / month</small>
            </div>
            <div class="chart-wrap chart-sm"><canvas ref="acquisitionChart"></canvas></div>
            <small class="text-muted d-block mt-1">First bar reflects the initial data backfill, not new signups.</small>
          </b-card>
        </b-col>
        <b-col lg="6">
          <b-card class="panel h-100" body-class="panel-body">
            <div slot="header" class="panel-head">
              <span><i class="icon-calendar mr-2"></i>Attendance Trend</span>
              <small class="text-muted">Last {{ days }} days</small>
            </div>
            <div class="chart-wrap chart-sm"><canvas ref="attendanceChart"></canvas></div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Sales channel + package tier + reseller stock -->
      <b-row class="mb-3">
        <b-col lg="5" class="mb-3 mb-lg-0">
          <b-card class="panel h-100" body-class="panel-body">
            <div slot="header" class="panel-head"><span><i class="icon-share-alt mr-2"></i>Clients by Sales Channel</span></div>
            <div class="chart-wrap chart-sm"><canvas ref="channelChart"></canvas></div>
          </b-card>
        </b-col>
        <b-col lg="4" class="mb-3 mb-lg-0">
          <b-card class="panel h-100" body-class="panel-body">
            <div slot="header" class="panel-head"><span><i class="icon-layers mr-2"></i>Package Tiers</span></div>
            <div class="chart-wrap chart-sm"><canvas ref="tierChart"></canvas></div>
          </b-card>
        </b-col>
        <b-col lg="3">
          <b-card class="panel h-100" body-class="panel-body">
            <div slot="header" class="panel-head"><span><i class="icon-drawer mr-2"></i>Reseller Stock</span></div>
            <div class="stock-wrap">
              <div class="stock-fig">
                <span class="stock-num">{{ fmt(stock.activated) }}</span>
                <span class="stock-cap">of {{ fmt(stock.stock) }} devices activated</span>
              </div>
              <div class="stock-bar">
                <div class="stock-bar-fill" :style="{ width: stockPct + '%' }"></div>
              </div>
              <div class="stock-legend">
                <div class="util-item"><span class="dot dot-active"></span>Activated<strong class="ml-auto">{{ fmt(stock.activated) }}</strong></div>
                <div class="util-item"><span class="dot dot-idle"></span>Remaining<strong class="ml-auto">{{ fmt(stock.remaining) }}</strong></div>
                <div class="util-item util-total"><span class="dot dot-total"></span>Total stock<strong class="ml-auto">{{ fmt(stock.stock) }}</strong></div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Top Resellers -->
      <b-card class="panel" body-class="p-0">
        <div slot="header" class="panel-head">
          <span><i class="icon-star mr-2"></i>Top Resellers</span>
          <small class="text-muted">{{ topResellers.length }} shown</small>
        </div>
        <b-table
          class="reseller-table mb-0"
          :items="topResellers"
          :fields="resellerFields"
          hover small responsive show-empty
          empty-text="No reseller data available"
        >
          <template v-slot:cell(rank)="row"><span class="rank-badge">{{ row.index + 1 }}</span></template>
          <template v-slot:cell(activations)="row"><span class="num">{{ fmt(row.item.activations) }}</span></template>
          <template v-slot:cell(totalDevices)="row"><span class="num">{{ fmt(row.item.totalDevices) }}</span></template>
          <template v-slot:cell(utilization)="row">
            <div class="mini-bar-wrap">
              <div class="mini-bar"><div class="mini-bar-fill" :style="{ width: rowUtil(row.item) + '%' }"></div></div>
              <span class="mini-bar-txt">{{ fmt(row.item.activeDevices) }} / {{ rowUtil(row.item) }}%</span>
            </div>
          </template>
        </b-table>
      </b-card>

    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import analyticsService from '@/services/analyticsService'

var PALETTE = {
  clients: '#3b82f6',
  devices: '#8b5cf6',
  active: '#10b981',
  employees: '#f59e0b'
}
var CATEGORICAL = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#6366f1', '#f97316']

export default {
  name: 'AnalyticsDashboard',
  data: function() {
    return {
      loading: false,
      unavailable: false,
      days: 15,
      dayOptions: [
        { value: 7,  text: 'Last 7 days' },
        { value: 15, text: 'Last 15 days' },
        { value: 30, text: 'Last 30 days' },
        { value: 60, text: 'Last 60 days' },
        { value: 90, text: 'Last 90 days' }
      ],
      summary: {
        totalClients: 0, totalDevices: 0, totalActiveDevices: 0, totalEmployees: 0,
        totalMobilePunchEmployees: 0, totalMobilePunches: 0, totalLocTrackingEmployees: 0,
        presentCount: 0, absentCount: 0, lateCount: 0, earlyExitCount: 0,
        effectiveHours: 0, expectedHours: 0, lateHours: 0
      },
      growthByDay: [],
      topResellers: [],
      acquisitionByMonth: [],
      channelBreakdown: [],
      packageTiers: [],
      stock: { stock: 0, activated: 0, remaining: 0 },
      kpiMeta: [
        { key: 'totalClients',       label: 'Total Clients',   series: 'clientCount',       color: PALETTE.clients },
        { key: 'totalDevices',       label: 'Total Devices',   series: 'deviceCount',       color: PALETTE.devices },
        { key: 'totalActiveDevices', label: 'Active Devices',  series: 'activeDeviceCount', color: PALETTE.active },
        { key: 'totalEmployees',     label: 'Total Employees', series: 'employeeCount',      color: PALETTE.employees }
      ],
      resellerFields: [
        { key: 'rank',         label: '#',           class: 'text-center col-rank' },
        { key: 'resellerName', label: 'Reseller',    sortable: true },
        { key: 'activations',  label: 'Activations', sortable: true, class: 'text-right' },
        { key: 'totalDevices', label: 'Devices',     sortable: true, class: 'text-right' },
        { key: 'utilization',  label: 'Active / Util.', class: 'col-util' }
      ]
    }
  },
  computed: {
    latestDate: function() {
      if (!this.growthByDay.length) return ''
      var last = this.growthByDay[this.growthByDay.length - 1]
      return last && last.date ? String(last.date).substring(0, 10) : ''
    },
    idleDevices: function() {
      return Math.max(0, (this.summary.totalDevices || 0) - (this.summary.totalActiveDevices || 0))
    },
    utilization: function() {
      var total = this.summary.totalDevices || 0
      return total ? Math.round((this.summary.totalActiveDevices || 0) / total * 100) : 0
    },
    ringDash: function() {
      var circ = 2 * Math.PI * 52
      var frac = this.utilization / 100
      return (circ * frac).toFixed(2) + ' ' + (circ * (1 - frac)).toFixed(2)
    },
    stockPct: function() {
      var total = this.stock.stock || 0
      return total ? Math.round((this.stock.activated || 0) / total * 100) : 0
    },
    cards: function() {
      var self = this
      return this.kpiMeta.map(function(m) {
        var series = self.growthByDay.map(function(r) { return Number(r[m.series]) || 0 })
        var delta = self.pctChange(series)
        return {
          key: m.key, label: m.label, color: m.color,
          value: self.summary[m.key] || 0,
          delta: delta,
          deltaText: delta === null ? '' : Math.abs(delta).toFixed(1) + '%',
          deltaClass: delta === null ? '' : (delta > 0 ? 'up' : (delta < 0 ? 'down' : 'flat')),
          deltaIcon: delta === null ? '' : (delta > 0 ? 'icon-arrow-up' : (delta < 0 ? 'icon-arrow-down' : 'icon-minus')),
          spark: self.spark(series)
        }
      })
    },
    engagement: function() {
      var s = this.summary
      var attTotal = (s.presentCount || 0) + (s.absentCount || 0)
      var attRate = attTotal ? Math.round((s.presentCount || 0) / attTotal * 100) : 0
      var wUtil = s.expectedHours ? Math.round((s.effectiveHours || 0) / s.expectedHours * 100) : 0
      return [
        { key: 'att',  label: 'Attendance Rate', color: '#10b981', icon: 'icon-check',
          value: attRate + '%', sub: this.fmt(s.presentCount) + ' present · ' + this.fmt(s.absentCount) + ' absent' },
        { key: 'util', label: 'Workforce Utilization', color: '#3b82f6', icon: 'icon-clock',
          value: wUtil + '%', sub: this.fmtK(s.effectiveHours) + ' / ' + this.fmtK(s.expectedHours) + ' hrs' },
        { key: 'mob',  label: 'Mobile Punch Users', color: '#8b5cf6', icon: 'icon-screen-smartphone',
          value: this.fmt(s.totalMobilePunchEmployees), sub: this.fmt(s.totalMobilePunches) + ' punches / day' },
        { key: 'loc',  label: 'Location Tracking', color: '#f59e0b', icon: 'icon-location-pin',
          value: this.fmt(s.totalLocTrackingEmployees), sub: 'employees tracked' }
      ]
    }
  },
  created: function() {
    // non-reactive chart registry + request sequence guard (both non-reactive on purpose)
    this.charts = { growth: null, acquisition: null, attendance: null, channel: null, tier: null }
    this.reqSeq = 0
  },
  mounted: function() {
    this.load()
  },
  beforeDestroy: function() {
    this.destroyCharts()
  },
  methods: {
    fmt: function(n) { return (Number(n) || 0).toLocaleString() },
    fmtK: function(n) {
      n = Number(n) || 0
      if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k'
      return String(Math.round(n))
    },
    pctChange: function(series) {
      if (!series || series.length < 2) return null
      var first = null
      for (var i = 0; i < series.length; i++) { if (series[i] > 0) { first = series[i]; break } }
      var last = series[series.length - 1]
      if (first === null || first === 0) return null
      return ((last - first) / first) * 100
    },
    rowUtil: function(item) {
      var total = Number(item.totalDevices) || 0
      return total ? Math.round((Number(item.activeDevices) || 0) / total * 100) : 0
    },
    spark: function(values) {
      var w = 120, h = 32, pad = 3
      if (!values || !values.length) return { points: '', area: '', cx: 0, cy: 0 }
      var min = Math.min.apply(null, values)
      var max = Math.max.apply(null, values)
      var range = (max - min) || 1
      var n = values.length
      var stepX = n > 1 ? (w - 2 * pad) / (n - 1) : 0
      var pts = values.map(function(v, i) {
        return { x: pad + i * stepX, y: pad + (h - 2 * pad) * (1 - (v - min) / range) }
      })
      var points = pts.map(function(p) { return p.x.toFixed(1) + ',' + p.y.toFixed(1) }).join(' ')
      var area = 'M ' + pts[0].x.toFixed(1) + ',' + (h - pad) +
        pts.map(function(p) { return ' L ' + p.x.toFixed(1) + ',' + p.y.toFixed(1) }).join('') +
        ' L ' + pts[n - 1].x.toFixed(1) + ',' + (h - pad) + ' Z'
      var last = pts[n - 1]
      return { points: points, area: area, cx: last.x.toFixed(1), cy: last.y.toFixed(1) }
    },
    load: function() {
      var self = this
      var reqId = ++self.reqSeq
      self.loading = true
      self.unavailable = false
      analyticsService.getSummary(self.days, function(data) {
        if (reqId !== self.reqSeq) return   // a newer request superseded this one — drop stale data
        self.loading = false
        if (data && data.available === false) { self.unavailable = true; return }
        self.summary = {
          totalClients:              data.totalClients              || 0,
          totalDevices:              data.totalDevices              || 0,
          totalActiveDevices:        data.totalActiveDevices        || 0,
          totalEmployees:            data.totalEmployees            || 0,
          totalMobilePunchEmployees: data.totalMobilePunchEmployees || 0,
          totalMobilePunches:        data.totalMobilePunches        || 0,
          totalLocTrackingEmployees: data.totalLocTrackingEmployees || 0,
          presentCount:              data.presentCount              || 0,
          absentCount:               data.absentCount               || 0,
          lateCount:                 data.lateCount                 || 0,
          earlyExitCount:            data.earlyExitCount            || 0,
          effectiveHours:            data.effectiveHours            || 0,
          expectedHours:             data.expectedHours             || 0,
          lateHours:                 data.lateHours                 || 0
        }
        self.growthByDay        = data.growthByDay        || []
        self.topResellers       = data.topResellers       || []
        self.acquisitionByMonth = data.acquisitionByMonth || []
        self.channelBreakdown   = data.channelBreakdown   || []
        self.packageTiers       = data.packageTiers       || []
        self.stock = {
          stock:     (data.resellerStock && Number(data.resellerStock.stock))     || 0,
          activated: (data.resellerStock && Number(data.resellerStock.activated)) || 0,
          remaining: (data.resellerStock && Number(data.resellerStock.remaining)) || 0
        }
        self.$nextTick(function() { self.renderCharts() })
      }, function(err) {
        if (reqId !== self.reqSeq) return
        self.loading = false
        self.$bvToast.toast(err, { title: 'Analytics Error', variant: 'danger', solid: true })
      })
    },
    destroyCharts: function() {
      var self = this
      if (!self.charts) return
      Object.keys(self.charts).forEach(function(k) {
        if (self.charts[k]) { self.charts[k].destroy(); self.charts[k] = null }
      })
    },
    renderCharts: function() {
      this.destroyCharts()
      this.renderGrowth()
      this.renderAcquisition()
      this.renderAttendance()
      this.renderChannel()
      this.renderTier()
    },
    baseLineOpts: function() {
      var self = this
      return {
        responsive: true, maintainAspectRatio: false,
        animation: { duration: self.reducedMotion() ? 0 : 600 },
        legend: { position: 'top', align: 'end', labels: { boxWidth: 12, usePointStyle: true, padding: 14, fontColor: '#5c6873' } },
        tooltips: { mode: 'index', intersect: false },
        hover: { mode: 'index', intersect: false },
        scales: {
          xAxes: [{ gridLines: { display: false, drawBorder: false }, ticks: { maxTicksLimit: 8, maxRotation: 0, autoSkip: true, fontColor: '#9aa5b1', fontSize: 11 } }],
          yAxes: [{ gridLines: { color: 'rgba(0,0,0,0.05)', drawBorder: false }, ticks: { beginAtZero: true, precision: 0, fontColor: '#9aa5b1', fontSize: 11, maxTicksLimit: 6 } }]
        }
      }
    },
    renderGrowth: function() {
      var self = this
      var canvas = self.$refs.growthChart
      if (!canvas || !self.growthByDay.length) return
      var ctx = canvas.getContext('2d')
      var grad = ctx.createLinearGradient(0, 0, 0, 260)
      grad.addColorStop(0, 'rgba(59,130,246,0.22)')
      grad.addColorStop(1, 'rgba(59,130,246,0.0)')
      var labels = self.growthByDay.map(function(r) { return r.date ? String(r.date).substring(5, 10) : '' })
      var pick = function(k) { return self.growthByDay.map(function(r) { return Number(r[k]) || 0 }) }
      self.charts.growth = new Chart(canvas, {
        type: 'line',
        data: { labels: labels, datasets: [
          { label: 'Clients', data: pick('clientCount'), borderColor: PALETTE.clients, backgroundColor: grad, pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12, fill: true, borderWidth: 2, lineTension: 0.3 },
          { label: 'Total Devices', data: pick('deviceCount'), borderColor: PALETTE.devices, backgroundColor: 'transparent', pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12, fill: false, borderWidth: 2, lineTension: 0.3 },
          { label: 'Active Devices', data: pick('activeDeviceCount'), borderColor: PALETTE.active, backgroundColor: 'transparent', pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12, fill: false, borderWidth: 2, borderDash: [5, 3], lineTension: 0.3 }
        ] },
        options: self.baseLineOpts()
      })
    },
    renderAttendance: function() {
      var self = this
      var canvas = self.$refs.attendanceChart
      if (!canvas || !self.growthByDay.length) return
      var labels = self.growthByDay.map(function(r) { return r.date ? String(r.date).substring(5, 10) : '' })
      var pick = function(k) { return self.growthByDay.map(function(r) { return Number(r[k]) || 0 }) }
      self.charts.attendance = new Chart(canvas, {
        type: 'line',
        data: { labels: labels, datasets: [
          { label: 'Present', data: pick('presentCount'), borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.12)', pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12, fill: true, borderWidth: 2, lineTension: 0.3 },
          { label: 'Absent', data: pick('absentCount'), borderColor: '#ef4444', backgroundColor: 'transparent', pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12, fill: false, borderWidth: 2, lineTension: 0.3 },
          { label: 'Late', data: pick('lateCount'), borderColor: '#f59e0b', backgroundColor: 'transparent', pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12, fill: false, borderWidth: 2, borderDash: [5, 3], lineTension: 0.3 }
        ] },
        options: self.baseLineOpts()
      })
    },
    renderAcquisition: function() {
      var self = this
      var canvas = self.$refs.acquisitionChart
      if (!canvas || !self.acquisitionByMonth.length) return
      var rows = self.acquisitionByMonth.slice(-12)
      var labels = rows.map(function(r) { return String(r.month) })
      var vals = rows.map(function(r) { return Number(r.newClients) || 0 })
      self.charts.acquisition = new Chart(canvas, {
        type: 'bar',
        data: { labels: labels, datasets: [{ label: 'New clients', data: vals, backgroundColor: '#3b82f6', borderRadius: 4, maxBarThickness: 34 }] },
        options: {
          responsive: true, maintainAspectRatio: false,
          animation: { duration: self.reducedMotion() ? 0 : 600 },
          legend: { display: false },
          tooltips: { intersect: false },
          scales: {
            xAxes: [{ gridLines: { display: false, drawBorder: false }, ticks: { fontColor: '#9aa5b1', fontSize: 10, maxRotation: 45, minRotation: 0 } }],
            yAxes: [{ gridLines: { color: 'rgba(0,0,0,0.05)', drawBorder: false }, ticks: { beginAtZero: true, precision: 0, fontColor: '#9aa5b1', fontSize: 11, maxTicksLimit: 5 } }]
          }
        }
      })
    },
    renderChannel: function() {
      var self = this
      var canvas = self.$refs.channelChart
      if (!canvas || !self.channelBreakdown.length) return
      var rows = self.channelBreakdown.slice(0, 8)
      var labels = rows.map(function(r) { return String(r.channel) })
      var vals = rows.map(function(r) { return Number(r.clients) || 0 })
      self.charts.channel = new Chart(canvas, {
        type: 'horizontalBar',
        data: { labels: labels, datasets: [{ label: 'Clients', data: vals, backgroundColor: '#6366f1', borderRadius: 4, maxBarThickness: 20 }] },
        options: {
          responsive: true, maintainAspectRatio: false,
          animation: { duration: self.reducedMotion() ? 0 : 600 },
          legend: { display: false },
          tooltips: { intersect: false },
          scales: {
            xAxes: [{ gridLines: { color: 'rgba(0,0,0,0.05)', drawBorder: false }, ticks: { beginAtZero: true, precision: 0, fontColor: '#9aa5b1', fontSize: 11, maxTicksLimit: 5 } }],
            yAxes: [{ gridLines: { display: false, drawBorder: false }, ticks: { fontColor: '#5c6873', fontSize: 11 } }]
          }
        }
      })
    },
    renderTier: function() {
      var self = this
      var canvas = self.$refs.tierChart
      if (!canvas || !self.packageTiers.length) return
      var rows = self.packageTiers.slice(0, 8)
      var labels = rows.map(function(r) { return String(r.tier) })
      var vals = rows.map(function(r) { return Number(r.clients) || 0 })
      self.charts.tier = new Chart(canvas, {
        type: 'doughnut',
        data: { labels: labels, datasets: [{ data: vals, backgroundColor: CATEGORICAL, borderWidth: 2, borderColor: '#fff' }] },
        options: {
          responsive: true, maintainAspectRatio: false, cutoutPercentage: 62,
          animation: { duration: self.reducedMotion() ? 0 : 600 },
          legend: { position: 'right', labels: { boxWidth: 10, usePointStyle: true, padding: 10, fontColor: '#5c6873', fontSize: 11 } },
          tooltips: { intersect: false }
        }
      })
    },
    reducedMotion: function() {
      return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
  }
}
</script>

<style scoped>
.analytics-dash { --accent: #3b82f6; }

/* Header */
.dash-title { font-weight: 700; letter-spacing: -0.01em; color: #23282c; }
.dash-title .icon-chart { color: #3b82f6; }
.range-select { width: 140px; }
.icon-refresh.spin { display: inline-block; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* KPI cards */
.kpi-row { margin-bottom: 0.25rem; }
.kpi-card {
  position: relative; background: #fff; border: 1px solid #e4e7ea; border-radius: 8px;
  padding: 14px 16px 8px; height: 100%; overflow: hidden;
  transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease;
}
.kpi-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--accent); }
.kpi-card:hover { box-shadow: 0 6px 18px rgba(24,40,72,0.10); transform: translateY(-2px); border-color: #d3d8dd; }
.kpi-label { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #8a94a0; }
.kpi-value { font-size: 1.9rem; font-weight: 700; line-height: 1.1; color: #23282c; font-variant-numeric: tabular-nums; margin: 2px 0 6px; }
.kpi-delta { display: inline-flex; align-items: center; gap: 2px; font-size: 0.72rem; font-weight: 700; padding: 2px 6px; border-radius: 10px; font-variant-numeric: tabular-nums; }
.kpi-delta i { font-size: 0.6rem; margin-right: 1px; }
.kpi-delta.up   { color: #0f9d58; background: rgba(16,185,129,0.12); }
.kpi-delta.down { color: #d93025; background: rgba(217,48,37,0.12); }
.kpi-delta.flat { color: #8a94a0; background: rgba(138,148,160,0.12); }
.kpi-spark { display: block; width: 100%; height: 32px; }
.spark-line { fill: none; stroke-width: 2; vector-effect: non-scaling-stroke; }
.spark-area { opacity: 0.12; }

/* Mini stat (engagement) */
.mini-stat { display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid #e4e7ea; border-radius: 8px; padding: 12px 14px; height: 100%; transition: box-shadow .18s ease, transform .18s ease; }
.mini-stat:hover { box-shadow: 0 6px 18px rgba(24,40,72,0.08); transform: translateY(-2px); }
.mini-icon { width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 1.15rem; flex-shrink: 0; }
.mini-body { min-width: 0; }
.mini-value { font-size: 1.45rem; font-weight: 700; line-height: 1.1; color: #23282c; font-variant-numeric: tabular-nums; }
.mini-label { font-size: 0.74rem; font-weight: 600; color: #5c6873; }
.mini-sub { font-size: 0.7rem; color: #9aa5b1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Panels */
.panel { border: 1px solid #e4e7ea; border-radius: 8px; }
.panel >>> .card-header { background: #fff; border-bottom: 1px solid #eceef0; padding: 0.7rem 1rem; border-radius: 8px 8px 0 0; }
.panel-head { display: flex; align-items: center; justify-content: space-between; font-weight: 600; color: #3e444a; }
.panel-head i { color: #8a94a0; }
.panel-body { padding: 1rem; }
.chart-wrap { position: relative; height: 300px; }
.chart-wrap.chart-sm { height: 250px; }

/* Utilization ring */
.util-wrap { display: flex; align-items: center; gap: 18px; }
.util-ring { position: relative; width: 130px; height: 130px; flex-shrink: 0; }
.util-ring svg { width: 100%; height: 100%; transform: rotate(-90deg); }
.ring-track { fill: none; stroke: #eceff2; stroke-width: 12; }
.ring-value { fill: none; stroke: #10b981; stroke-width: 12; stroke-linecap: round; transition: stroke-dasharray .6s ease; }
.util-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.util-pct { font-size: 1.7rem; font-weight: 700; color: #23282c; line-height: 1; font-variant-numeric: tabular-nums; }
.util-cap { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: #8a94a0; }
.util-legend { flex: 1; min-width: 0; }
.util-item { display: flex; align-items: center; font-size: 0.82rem; color: #5c6873; padding: 4px 0; border-bottom: 1px dashed #eceef0; }
.util-item:last-child { border-bottom: 0; }
.util-item strong { color: #23282c; font-variant-numeric: tabular-nums; }
.util-item .dot { width: 9px; height: 9px; border-radius: 50%; margin-right: 8px; }
.dot-active { background: #10b981; }
.dot-idle { background: #cbd2d9; }
.dot-total { background: #8b5cf6; }

/* Reseller stock */
.stock-wrap { display: flex; flex-direction: column; gap: 10px; }
.stock-fig { line-height: 1.15; }
.stock-num { font-size: 1.7rem; font-weight: 700; color: #23282c; font-variant-numeric: tabular-nums; }
.stock-cap { display: block; font-size: 0.72rem; color: #9aa5b1; }
.stock-bar { height: 8px; border-radius: 4px; background: #eceff2; overflow: hidden; }
.stock-bar-fill { height: 100%; border-radius: 4px; background: #10b981; transition: width .5s ease; }

/* Reseller table */
.reseller-table >>> thead th { border-top: 0; border-bottom: 1px solid #eceef0; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em; color: #8a94a0; font-weight: 600; }
.reseller-table >>> td { vertical-align: middle; }
.reseller-table >>> tbody tr:hover { background: #f6f8fa; }
.reseller-table .num { font-variant-numeric: tabular-nums; font-weight: 600; color: #3e444a; }
.reseller-table >>> .col-rank { width: 44px; }
.reseller-table >>> .col-util { width: 190px; }
.rank-badge { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 6px; background: #eef2f7; color: #5c6873; font-size: 0.72rem; font-weight: 700; }
.mini-bar-wrap { display: flex; align-items: center; gap: 8px; }
.mini-bar { flex: 1; height: 6px; border-radius: 3px; background: #eceff2; overflow: hidden; min-width: 60px; }
.mini-bar-fill { height: 100%; border-radius: 3px; background: #10b981; transition: width .4s ease; }
.mini-bar-txt { font-size: 0.72rem; color: #8a94a0; font-variant-numeric: tabular-nums; white-space: nowrap; }

/* Skeletons */
.skeleton-card { background: linear-gradient(90deg, #eef1f4 25%, #f6f8fa 37%, #eef1f4 63%); background-size: 400% 100%; border-radius: 8px; height: 104px; animation: shimmer 1.3s ease infinite; }
.skeleton-chart { height: 340px; }
@keyframes shimmer { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }

@media (prefers-reduced-motion: reduce) {
  .kpi-card, .mini-stat, .ring-value, .mini-bar-fill, .stock-bar-fill { transition: none; }
  .skeleton-card { animation: none; }
  .icon-refresh.spin { animation: none; }
}
</style>
